/**
 * Bank Balances diagnostic card.
 *
 * Lists every account with entity resolution, native balance, as-of,
 * stale flag, and reconciliation (account rows sum to per-entity totals
 * sum to grand total, in both native-per-currency and converted terms).
 */

import { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { bankAccountsAPI } from '../../lib/api';
import { convertAmount } from '../../lib/currencyConvert';
import { formatCurrencyFull } from '../../lib/chartFormatters';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatAsOf(isoStr) {
  if (!isoStr) return 'None';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

function isStale(isoStr) {
  if (!isoStr) return true;
  try {
    var age = Date.now() - new Date(isoStr).getTime();
    return age > 86400000; // 24h
  } catch (_) {
    return true;
  }
}

/** Run reconciliation checks and return a list of pass/fail results. */
function runReconChecks(data, fxRates) {
  var checks = [];
  var displayCurrency = 'GBP';

  // 1. Sum account rows per currency
  var accountNativeTotals = {};
  var accountConvertedTotal = 0;
  for (var i = 0; i < data.accounts.length; i++) {
    var acc = data.accounts[i];
    var bal = acc.balance != null ? Number(acc.balance) : 0;
    accountNativeTotals[acc.currency] = (accountNativeTotals[acc.currency] ?? 0) + bal;
    var conv = convertAmount(bal, acc.currency, displayCurrency, fxRates);
    accountConvertedTotal += conv ?? 0;
  }

  // 2. Sum per-entity subtotals per currency
  var entityNativeTotals = {};
  var entityConvertedTotal = 0;
  for (var eid in data.by_entity) {
    var entity = data.by_entity[eid];
    for (var cur in entity.currency_totals) {
      var amt = Number(entity.currency_totals[cur]);
      entityNativeTotals[cur] = (entityNativeTotals[cur] ?? 0) + amt;
      var c = convertAmount(amt, cur, displayCurrency, fxRates);
      entityConvertedTotal += c ?? 0;
    }
  }

  // Check: native totals match
  var allCurrencies = new Set([...Object.keys(accountNativeTotals), ...Object.keys(entityNativeTotals)]);
  var nativeMatch = true;
  for (var cur2 of allCurrencies) {
    var acctAmt = accountNativeTotals[cur2] ?? 0;
    var entityAmt = entityNativeTotals[cur2] ?? 0;
    if (Math.abs(acctAmt - entityAmt) > 0.01) {
      nativeMatch = false;
    }
  }
  checks.push({
    label: 'Native totals: account rows == per-entity subtotals',
    pass: nativeMatch,
    detail: nativeMatch ? 'Match' : 'MISMATCH',
  });

  // Check: converted totals match
  var convertedMatch = Math.abs(accountConvertedTotal - entityConvertedTotal) < 0.01;
  checks.push({
    label: 'Converted total: sum(accounts) == sum(entity subtotals)',
    pass: convertedMatch,
    detail: convertedMatch
      ? formatCurrencyFull(accountConvertedTotal, displayCurrency)
      : 'Account sum: ' + formatCurrencyFull(accountConvertedTotal, displayCurrency) +
        ' vs Entity sum: ' + formatCurrencyFull(entityConvertedTotal, displayCurrency),
  });

  // Check: negative balances preserved
  var hasNegative = data.accounts.some(function (a) {
    return a.balance != null && Number(a.balance) < 0;
  });
  checks.push({
    label: 'Negative balance preservation',
    pass: true,
    detail: hasNegative ? 'Found negative balances (preserved correctly)' : 'No negative balances in data',
  });

  // Check: unassigned accounts not in named entity totals
  var unassignedInEntity = false;
  if (data.by_entity['unassigned']) {
    unassignedInEntity = true;
  }
  checks.push({
    label: 'Unassigned accounts excluded from entity totals',
    pass: !unassignedInEntity,
    detail: unassignedInEntity
      ? 'WARNING: "unassigned" found in by_entity'
      : data.unassigned.length + ' unassigned accounts (correctly excluded)',
  });

  return checks;
}

/** Check scope isolation: personal and entities responses share no assigned accounts.
 *  Unassigned accounts may appear in both scopes (no resolved group_role). */
function runScopeIsolationCheck(entitiesData, personalData) {
  var entitiesAssigned = new Set(
    entitiesData.accounts
      .filter(function (a) { return a.entity_id !== 'unassigned'; })
      .map(function (a) { return a.id; })
  );
  var personalAssigned = new Set(
    personalData.accounts
      .filter(function (a) { return a.entity_id !== 'unassigned'; })
      .map(function (a) { return a.id; })
  );
  var overlap = [];
  for (var id of personalAssigned) {
    if (entitiesAssigned.has(id)) overlap.push(id);
  }
  return {
    label: 'Scope isolation: personal excludes entities and vice versa',
    pass: overlap.length === 0,
    detail: overlap.length === 0
      ? entitiesAssigned.size + ' entity accounts, ' + personalAssigned.size + ' personal accounts, 0 overlap'
      : 'OVERLAP: ' + overlap.length + ' assigned account(s) appear in both scopes',
  };
}

export function BankBalancesDiagnosticPanel() {
  var [loading, setLoading] = useState(false);
  var [syncing, setSyncing] = useState(false);
  var [data, setData] = useState(null);
  var [checks, setChecks] = useState(null);

  var runDiagnostic = async function () {
    setLoading(true);
    setData(null);
    setChecks(null);
    try {
      // Fetch all three scopes in parallel for reconciliation
      var [allRes, entitiesRes, personalRes] = await Promise.all([
        bankAccountsAPI.getSummary({}),
        bankAccountsAPI.getSummary({ scope: 'entities' }),
        bankAccountsAPI.getSummary({ scope: 'personal' }),
      ]);
      var d = allRes.data;
      setData(d);
      var allChecks = runReconChecks(d, d.fx_rates ?? {});
      allChecks.push(runScopeIsolationCheck(entitiesRes.data, personalRes.data));
      setChecks(allChecks);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handleSync = async function () {
    setSyncing(true);
    try {
      await bankAccountsAPI.sync();
      toast.success('Bank accounts synced');
      // Re-run diagnostic after sync
      await runDiagnostic();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSyncing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bank Balances Diagnostic</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={runDiagnostic} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Diagnostic
          </Button>
          <Button variant="outline" onClick={handleSync} disabled={syncing || loading}>
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync Now
          </Button>
        </div>

        {data && (
          <div className="space-y-4">
            {/* Reconciliation checks */}
            {checks && (
              <div className="space-y-1">
                <p className="text-sm font-semibold">Reconciliation Checks</p>
                {checks.map(function (chk, i) {
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {chk.pass ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                      )}
                      <span className={chk.pass ? 'text-muted-foreground' : 'text-red-600 font-medium'}>
                        {chk.label}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto">{chk.detail}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            <div className="text-sm">
              <p className="font-semibold">
                {data.account_count} accounts, {Object.keys(data.by_entity).length} entities
              </p>
              {data.newest_as_of && (
                <p className="text-xs text-muted-foreground">
                  Newest balance: {formatAsOf(data.newest_as_of)}
                  {data.stale && (
                    <Badge variant="outline" className="ml-2 text-amber-600 border-amber-400 text-[10px] px-1.5 py-0">
                      Stale
                    </Badge>
                  )}
                </p>
              )}
            </div>

            {/* FX rates */}
            {Object.keys(data.fx_rates ?? {}).length > 0 && (
              <div className="text-sm">
                <p className="font-semibold">FX Rates</p>
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(data.fx_rates).map(function (entry) {
                    return (
                      <span key={entry[0]} className="text-xs font-mono text-muted-foreground">
                        {entry[0]}: {entry[1]}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Per-account table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Account</th>
                    <th className="py-1.5 pr-3 font-medium">Entity</th>
                    <th className="py-1.5 pr-3 font-medium">Provider</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Balance</th>
                    <th className="py-1.5 pr-3 font-medium">Cur</th>
                    <th className="py-1.5 pr-3 font-medium">As-of</th>
                    <th className="py-1.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map(function (acc) {
                    var staleFlag = isStale(acc.balance_as_of);
                    var isUnassigned = acc.entity_id === 'unassigned';
                    return (
                      <tr key={acc.id} className={'border-b border-border/50' + (isUnassigned ? ' bg-amber-50 dark:bg-amber-900/10' : '')}>
                        <td className="py-1.5 pr-3">{acc.account_name}</td>
                        <td className="py-1.5 pr-3">
                          {isUnassigned ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">
                              Unassigned
                            </Badge>
                          ) : (
                            <span className="text-xs">{acc.entity_name}</span>
                          )}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{acc.bank_name}</td>
                        <td className={'py-1.5 pr-3 text-right font-mono' + (acc.balance == null ? ' text-muted-foreground' : '')}>
                          {acc.balance != null ? formatCurrencyFull(Number(acc.balance), acc.currency) : '--'}
                        </td>
                        <td className="py-1.5 pr-3 text-xs">{acc.currency}</td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{formatAsOf(acc.balance_as_of)}</td>
                        <td className="py-1.5">
                          {staleFlag ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] px-1.5 py-0">
                              <Clock className="h-3 w-3 mr-0.5" />
                              Stale
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600 border-green-400 text-[10px] px-1.5 py-0">
                              Fresh
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
